import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { CompanyDashboardInsight } from '../../models/company.dashboard.insight.model';
import { CompanyDashboardComparsion } from '../../models/company.dashboard.comparsion.model';
import { CompanyDashboardAnalysis } from '../../models/company.dashboard.analysis.model';
import { DashboardInsight } from '../../models/dashboard.insight.model';
import { DashboardComparsion } from '../../models/dashboard.comparsion.model';
import { DashboardAnalysis } from '../../models/dashboard.analysis.model';
import { authToken } from '../../scripts/authentication.script';

function abbreviate(value: number): string {
  if (value === 0) return '0'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`
  return `${sign}${abs.toFixed(2)}`
}

function trendString(current: number, previous: number): string {
  if (previous === 0 && current === 0) return '0%'
  if (previous === 0) return '+100%'
  const pct = ((current - previous) / Math.abs(previous)) * 100
  const rounded = Math.round(pct)
  if (rounded === 0) return '0%'
  return rounded > 0 ? `+${rounded}%` : `${rounded}%`
}

function mapInsightResponse(raw: any, id_company: string, time_interval_range: string, time_interval_current_start_date: string, time_interval_current_end_date: string, time_interval_compare_start_date: string, time_interval_compare_end_date: string): CompanyDashboardInsight {
  const payload = raw?.data ?? raw
  const cur = payload?.current ?? {}
  const cmp = payload?.comparison ?? {}

  const totalExpenses = cur.total_expenses ?? 0
  const totalRevenue = cur.total_revenue ?? 0
  const netProfit = cur.net_profit ?? 0
  const invoiceCount = cur.invoice_count ?? 0
  const expenseCount = cur.expense_count ?? 0
  const totalDebt = cur.total_debt ?? 0
  const totalPaid = cur.total_paid ?? 0
  const quotesApproved = cur.quotes_approved ?? 0
  const ordersToInvoice = cur.orders_to_invoice ?? 0

  const cmpExpenses = cmp?.total_expenses ?? 0
  const cmpRevenue = cmp?.total_revenue ?? 0
  const cmpDebt = cmp?.total_debt ?? 0
  const cmpPaid = cmp?.total_paid ?? 0
  const cmpQuotesApproved = cmp?.quotes_approved ?? 0
  const cmpOrdersToInvoice = cmp?.orders_to_invoice ?? 0

  const totalInvoiced = totalDebt + totalPaid
  const debtPct = totalInvoiced > 0 ? ((totalDebt / totalInvoiced) * 100).toFixed(2) + '%' : '0%'
  const paidPct = totalInvoiced > 0 ? ((totalPaid / totalInvoiced) * 100).toFixed(2) + '%' : '0%'
  const quotesTotal = cur.quotes_total ?? (quotesApproved > 0 ? quotesApproved : 1)
  const quotesApprovedPct = quotesTotal > 0 ? ((quotesApproved / quotesTotal) * 100).toFixed(2) + '%' : '0%'

  const insight: DashboardInsight = {
    general: {
      expenses: {
        total: totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        trend: trendString(totalExpenses, cmpExpenses),
        abbreviation: abbreviate(totalExpenses)
      },
      turnout: {
        total: totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        trend: trendString(totalRevenue, cmpRevenue),
        abbreviation: abbreviate(totalRevenue)
      },
      amount_orders_to_billed: {
        total: ordersToInvoice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        trend: trendString(ordersToInvoice, cmpOrdersToInvoice),
        abbreviation: abbreviate(ordersToInvoice)
      },
      amount_debt: {
        total: totalDebt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        trend: trendString(totalDebt, cmpDebt),
        abbreviation: abbreviate(totalDebt),
        percentage: debtPct
      },
      amount_paid: {
        total: totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        trend: trendString(totalPaid, cmpPaid),
        abbreviation: abbreviate(totalPaid),
        percentage: paidPct
      },
      quotes_approved: {
        total: quotesApproved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        trend: trendString(quotesApproved, cmpQuotesApproved),
        abbreviation: abbreviate(quotesApproved),
        percentage: quotesApprovedPct
      }
    },
    quotes: payload?.quotes ?? [],
    orders: payload?.orders ?? [],
    invoices: payload?.invoices ?? []
  }

  return {
    id_company,
    time_interval: {
      range: time_interval_range,
      current: { start_date: time_interval_current_start_date, end_date: time_interval_current_end_date },
      compare: { start_date: time_interval_compare_start_date, end_date: time_interval_compare_end_date }
    },
    data: insight
  }
}

function mapComparsionResponse(raw: any, id_company: string, range_current: string): CompanyDashboardComparsion {
  const payload = raw?.data ?? raw
  const months = payload?.months ?? []
  const year = payload?.year ?? (parseInt(range_current) || new Date().getFullYear())
  const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']

  const amounts: DashboardComparsion['amounts'] = months.map((m: any) => ({
    month: monthNames[(m.month ?? 1) - 1] ?? `M${m.month}`,
    total_sales: m.current_revenue ?? m.revenue ?? 0,
    total_purchases: m.current_expenses ?? m.expenses ?? 0
  }))

  const yearList: string[] = []
  for (let y = year; y >= year - 4; y--) {
    yearList.push(String(y))
  }

  return {
    id_company,
    range: { list: yearList, current: range_current },
    data: { amounts }
  }
}

function mapAnalysisResponse(raw: any, id_company: string): CompanyDashboardAnalysis {
  const payload = raw?.data ?? raw

  const analysis: DashboardAnalysis = {
    quotes_expire: {
      total: payload?.quotes_expire?.total ?? 0,
      list: payload?.quotes_expire?.list ?? []
    },
    invoices_debt: {
      total: payload?.invoices_debt?.total ?? 0,
      list: payload?.invoices_debt?.list ?? []
    },
    customer_payments: {
      best: payload?.customer_payments?.best ?? [],
      worst: payload?.customer_payments?.worst ?? []
    }
  }

  return {
    id_company,
    data: analysis
  }
}

export class DashboardDA {
  static apiResponse: ApiResponse

  public static async getDashboardInsight(
    id_company: string,
    time_interval_range: string,
    time_interval_current_start_date: string,
    time_interval_current_end_date: string,
    time_interval_compare_start_date: string,
    time_interval_compare_end_date: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyDashboardInsight: CompanyDashboardInsight | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/dashboard.insight.data.json').then(response => { companyDashboardInsight = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyDashboardInsight!.id_company === id_company) {
        companyDashboardInsight!.time_interval = { range: time_interval_range, current: { start_date: time_interval_current_start_date, end_date: time_interval_current_end_date }, compare: { start_date: time_interval_compare_start_date, end_date: time_interval_compare_end_date } }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyDashboardInsight
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      const from = time_interval_current_start_date.substring(0, 10)
      const to = time_interval_current_end_date.substring(0, 10)
      const compareFrom = time_interval_compare_start_date.substring(0, 10)
      const compareTo = time_interval_compare_end_date.substring(0, 10)
      const qs = `from=${from}&to=${to}&compare_from=${compareFrom}&compare_to=${compareTo}`
      const commerceUrl = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/reports/dashboard/insight?${qs}`
      const posUrl = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos/reports/dashboard/insight?${qs}`

      const config: any = {
        headers: { Authorization: authToken() }
      }

      const [commerceResult, posResult] = await Promise.allSettled([
        axios.get(commerceUrl, config),
        axios.get(posUrl, config)
      ])

      const commerceOk = commerceResult.status === 'fulfilled' && commerceResult.value.status >= 200 && commerceResult.value.status < 300
      const posOk = posResult.status === 'fulfilled' && posResult.value.status >= 200 && posResult.value.status < 300

      if (!commerceOk && !posOk) {
        const err = commerceResult.status === 'rejected' ? commerceResult.reason : (commerceResult.value as any)
        this.apiResponse = {
          status: err?.response?.status || 500,
          message: err?.response?.statusText || 'Error',
          data: err?.response?.data?.error || { code: 'NETWORK_ERROR', message: 'Both commerce and POS insight failed' }
        }
      } else {
        const commerceRaw = commerceOk ? (commerceResult as PromiseFulfilledResult<any>).value.data : {}
        const posRaw = posOk ? ((posResult as PromiseFulfilledResult<any>).value.data?.data ?? (posResult as PromiseFulfilledResult<any>).value.data) : {}
        const posCur = posRaw?.current ?? {}
        const posCmp = posRaw?.comparison ?? {}

        // Merge POS total_sales into commerce total_revenue
        const merged = JSON.parse(JSON.stringify(commerceRaw))
        const mergedPayload = merged?.data ?? merged
        if (mergedPayload?.current) {
          mergedPayload.current.total_revenue = (mergedPayload.current.total_revenue ?? 0) + (posCur.total_sales ?? 0)
        }
        if (mergedPayload?.comparison) {
          mergedPayload.comparison.total_revenue = (mergedPayload.comparison.total_revenue ?? 0) + (posCmp.total_sales ?? 0)
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: mapInsightResponse(merged, id_company, time_interval_range, time_interval_current_start_date, time_interval_current_end_date, time_interval_compare_start_date, time_interval_compare_end_date)
        }
      }
    }

    return this.apiResponse
  }

  public static async getDashboardComparsion(
    id_company: string,
    range_current: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyDashboardComparsion: CompanyDashboardComparsion | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/dashboard.comparsion.data.json').then(response => { companyDashboardComparsion = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyDashboardComparsion!.id_company === id_company) {
        companyDashboardComparsion!.range.current = range_current

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyDashboardComparsion
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      const commerceUrl = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/reports/dashboard/comparison?year=${range_current}`
      const posUrl = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/pos/reports/dashboard/comparison?year=${range_current}`

      const config: any = {
        headers: { Authorization: authToken() }
      }

      const [commerceResult, posResult] = await Promise.allSettled([
        axios.get(commerceUrl, config),
        axios.get(posUrl, config)
      ])

      const commerceOk = commerceResult.status === 'fulfilled' && commerceResult.value.status >= 200 && commerceResult.value.status < 300
      const posOk = posResult.status === 'fulfilled' && posResult.value.status >= 200 && posResult.value.status < 300

      if (!commerceOk && !posOk) {
        const err = commerceResult.status === 'rejected' ? commerceResult.reason : (commerceResult.value as any)
        this.apiResponse = {
          status: err?.response?.status || 500,
          message: err?.response?.statusText || 'Error',
          data: err?.response?.data?.error || { code: 'NETWORK_ERROR', message: 'Both commerce and POS comparison failed' }
        }
      } else {
        const commerceRaw = commerceOk ? (commerceResult as PromiseFulfilledResult<any>).value.data : {}
        const posRaw = posOk ? ((posResult as PromiseFulfilledResult<any>).value.data?.data ?? (posResult as PromiseFulfilledResult<any>).value.data) : {}
        const posMonths: any[] = posRaw?.months ?? []

        // Build a lookup of POS monthly sales by month number
        const posMonthMap: Record<number, number> = {}
        for (const pm of posMonths) {
          posMonthMap[pm.month] = pm.total_sales ?? 0
        }

        // Merge POS sales into commerce months by month number
        const merged = JSON.parse(JSON.stringify(commerceRaw))
        const mergedPayload = merged?.data ?? merged
        if (mergedPayload?.months && Array.isArray(mergedPayload.months)) {
          for (const m of mergedPayload.months) {
            const posSales = posMonthMap[m.month] ?? 0
            m.current_revenue = (m.current_revenue ?? m.revenue ?? 0) + posSales
          }
        }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: mapComparsionResponse(merged, id_company, range_current)
        }
      }
    }

    return this.apiResponse
  }

  public static async getDashboardAnalysis(
    id_company: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyDashboardAnalysis: CompanyDashboardAnalysis | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/dashboard.analysis.data.json').then(response => { companyDashboardAnalysis = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyDashboardAnalysis!.id_company === id_company) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyDashboardAnalysis
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/reports/dashboard/analysis`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: mapAnalysisResponse(response.data, id_company)
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status || 500,
          message: error.response?.statusText || 'Error',
          data: error.response?.data?.error || { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }
}
